import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Form,
  Modal,
  Row,
  Col,
  Spinner,
  Tooltip, OverlayTrigger,
} from 'react-bootstrap';
import { FileUploader } from 'react-drag-drop-files';
import Select from 'react-select';
import validator from 'validator';
import config from '../../config/Constant';
import { postRequest } from '../../helpers/apiHandler';
import { ExcelExampleModel, TxtExampleModel } from '../../components/Contact/ImportAddContactModal/ExcelExampleImportModel';
import CustomAlert from '../../components/CustomAlert';
import UploadIconSVG from '../SVG/UploadIconSVG'
import ContactUploadErrorModel from './ContactsUploadError';
import CloseIcon from '../SVG/CloseIcon';
import AddContactIcon from '../../assets/icon-add-contact.svg'
import {
  reloadContactList,
  reloadContactOverview,
} from '../../store/actions/reduxAction';
import CustomTooltip from '../../components/CustomTooltip';
import ClosableChip from '../../components/ClosableChip';

const ContactUpload = ({
  contactList,
  onImport = () => { },
  openModalUpload,
  setOpenModalUpload,
}) => {
  const dispatch = useDispatch();
  const { access_token } = useSelector((state) => state.auth)

  const [loading, setLoading] = useState(false);
  const [showUploadErrorModel, setUploadErrorModel] = useState(false);
  const [uploadErrorData, setUploadErrorData] = useState(false);
  const [selectedFile, setSelectedFile] = useState();
  const [selectedList, setSelectedList] = useState('');
  const [contactName, setContactName] = useState('');
  const [uploadMethod, setUploadMethod] = useState('');
  const [contactNameError, setContactNameError] = useState({ error: false, message: '' });
  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const [selectedOption, setSelectedOption] = useState('create');

  const handleCloseModel = () => {
    setOpenModalUpload(false);
    setSelectedFile();
    setSelectedList('');
    setContactName('');
    setUploadMethod('');
    setContactNameError(({ error: false, message: '' }));
  };

  const handleUploadFile = (files) => {
    setSelectedFile(files);
  };

  const handleClearUploadedFile = () => {
    setSelectedFile();
  }

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  }

  const handleSubmitContactForm = (evt) => {
    evt.preventDefault();
    const form = evt.currentTarget;

    if (form.checkValidity() === false) {
      evt.stopPropagation();
    }

    if (!selectedList.value && !validator.isLength(contactName.trim(), { min: 2, max: 30 })) {
      setContactNameError({
        error: true,
        message: 'Contact name cannot more than 30 characters long.',
      });
      return;
    }

    if ((selectedList === '' || selectedList.value === undefined) && validator.isEmpty(contactName)) {
      setContactNameError({
        error: true,
        message: 'Contact cannot be empty.',
      });
      return;
    }

    setLoading(true);
    let formData = new FormData();
    if (uploadMethod === 'name') formData.append('lead_list_name', contactName.trim());
    if (uploadMethod === 'list') formData.append('lead_list_id', selectedList.value);

    formData.append('file', selectedFile);

    postRequest('/v2/contacts-upload', formData, access_token.token, (res) => {
      if (res.success) {
        setAlertState({
          show: true,
          message: 'Contact(s) successfully uploaded',
          variant: 'success',
        });
        onImport(res.data);
        // Dispatch the reload mechanisms
        dispatch(reloadContactList());
        dispatch(reloadContactOverview());
        // Gracefully close the modal
        handleCloseModel();
      } else if (res.code === 11111) {
        setUploadErrorModel(true);
        setUploadErrorData(res.data);

        setAlertState({
          show: true,
          message: res.message,
          variant: 'danger',
        });
      } else if (res.code === 12200) {
        setAlertState({
          show: true,
          message: 'Contact List name must be unique',
          variant: 'danger',
        });

        // Reload the contact list and contact overview here too
        dispatch(reloadContactList());
        dispatch(reloadContactOverview());
      }

      setLoading(false);
    });
  };

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <>
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
      />
      <Modal
        show={openModalUpload}
        animation={true}
        onHide={handleCloseModel}
        className="modal-form-contact modal-upload-contact"
        centered
      >
        <ContactUploadErrorModel
          show={showUploadErrorModel}
          onHide={setUploadErrorModel}
          data={uploadErrorData}
        />

        <Modal.Body>
          <CloseIcon handleModalClose={handleCloseModel} />
          <div className="svg-wrapper">
            <img src={AddContactIcon} />
          </div>
          <Modal.Title as="h4" style={{ margin: '8px 0 16px' }}>
            Import Contacts
          </Modal.Title>
          <p>Before uploading, please ensure your file matches our formatting guidelines. Reference our sample files for help:</p>
          <Form noValidate onSubmit={handleSubmitContactForm}>
            <Row>
              <Col xs={12} md={6}>
                <ExcelExampleModel />
              </Col>
              <Col xs={12} md={6}>
                <TxtExampleModel />
              </Col>
            </Row>
            <Form.Group>
              <FileUploader
                classes="w-100"
                name="file"
                label="Upload File"
                types={['CSV', 'TXT']}
                handleChange={handleUploadFile}
              >
                <div className="file-uploader">
                  <div className="file-uploader-title">
                    Drag & Drop files here or <span className='browse-link'>Browse files</span>
                  </div>
                  {!selectedFile ? <UploadIconSVG fill="#0BA9E0" /> : <div
                    className="file-loaded-message">
                    <ClosableChip text={selectedFile.name} onClick={handleClearUploadedFile} />
                  </div>}
                  <div className="file-upload-text">
                    Every number needs to include a country code for a successful import.
                  </div>
                  <div className="file-upload-accept">Supported format: CSV & TXT</div>
                </div>
              </FileUploader>
            </Form.Group>
            <h5>
              Contact List
            </h5>
            <Form.Group>
              <div className='radio-container'>
                <input id='update' type='radio' value='update' checked={selectedOption === 'update'} onChange={handleOptionChange} className='mr-2' />
                <label htmlFor="update" className='radio-label'>
                  Create New Contact List <CustomTooltip id='create-tooltip' placement='right' message='Use Contact List to organize your contacts into groups. Ensure the Contact List Name is unique.' />
                </label>
              </div>
              {selectedOption === 'update' ? <>
                <Form.Control
                  maxLength={30}
                  placeholder="Enter New Contact List Name"
                  value={contactName}
                  onChange={(e) => {
                    setSelectedList('');
                    setContactNameError({ error: false, message: '' });
                    setUploadMethod('name');
                    setContactName(e.target.value);
                  }}
                  isInvalid={contactNameError.error}
                />
                <Form.Control.Feedback type="invalid">{contactNameError.message}</Form.Control.Feedback>
              </> : <></>}
            </Form.Group>
            <Form.Group>
              <div className='radio-container'>
                <input id='create' type='radio' value='create' checked={selectedOption === 'create'} onChange={handleOptionChange} className='mr-2' />
                <label htmlFor="create" className='radio-label'>
                  Add into existing Contact List <CustomTooltip id='add-existing-tooltip' placement='right' message='Select one contact list from the dropdown below.' />
                </label>
              </div>
              {selectedOption === 'create' ? <Select
                placeholder="Select Contact List"
                isClearable={false}
                options={contactList.map((contact) => ({
                  value: contact.id,
                  label: `${contact.name} (${contact.count})`,
                }))}
                value={selectedList}
                onChange={(value) => {
                  setSelectedList(value);
                  setContactName('');
                  setContactNameError({ error: false, message: '' });
                  setUploadMethod('list');
                  if (value === null) {
                    setSelectedList('');
                  }
                }}
                className={`react-select ${contactNameError.error ? 'is-invalid' : ''}`}
                styles={{
                  placeholder: (defaultStyles) => {
                    return {
                      ...defaultStyles,
                      color: '#BDBDBD',
                    }
                  }
                }}
              /> : <></>}
            </Form.Group>
            <Form.Group>
              <Button type="submit" variant="primary" disabled={loading} style={{ minWidth: 160 }}>
                {loading && <Spinner animation="border" size="sm" className="mr-2" />}
                Upload
              </Button>
            </Form.Group>
          </Form>
        </Modal.Body>
      </Modal>
    </>

  );
};

export default ContactUpload;
